// @ts-nocheck
import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { MobileFormSheet } from '../ui/MobileFormSheet';

type Client = { id: string; name: string };
type Case = { id: string; title: string };

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: () => void;
};

const CATEGORIES_INCOME = [
  'Honorários contratuais',
  'Honorários de êxito',
  'Consulta',
  'Custas recebidas',
  'Outros',
];

const CATEGORIES_EXPENSE = [
  'Custas processuais',
  'Honorários a pagar',
  'Despesas administrativas',
  'Impostos',
  'Outros',
];

const parseMoney = (s: string) => {
  const n = Number(
    s
      .replace(/\s/g, '')
      .replace(/R\$/g, '')
      .replace(/\./g, '')
      .replace(',', '.'),
  );
  return Number.isFinite(n) ? n : 0;
};

export const MobileFinancialQuickForm = ({
  open,
  onOpenChange,
  onCreated,
}: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);

  const [form, setForm] = useState({
    entry_type: 'receita' as 'receita' | 'despesa',
    description: '',
    amount: '',
    category: '',
    due_date: new Date().toISOString().slice(0, 10),
    client_id: '',
    case_id: '',
  });

  useEffect(() => {
    if (!open || !user) return;
    const load = async () => {
      const [cRes, caRes] = await Promise.all([
        supabase
          .from('clients')
          .select('id, name')
          .eq('user_id', user.id)
          .order('name'),
        supabase
          .from('cases')
          .select('id, title')
          .eq('user_id', user.id)
          .neq('archived', true)
          .order('created_at', { ascending: false }),
      ]);
      setClients(cRes.data || []);
      setCases(caRes.data || []);
    };
    load();
  }, [open, user]);

  useEffect(() => {
    if (open) {
      setForm({
        entry_type: 'receita',
        description: '',
        amount: '',
        category: '',
        due_date: new Date().toISOString().slice(0, 10),
        client_id: '',
        case_id: '',
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast({ title: 'Informe a descrição', variant: 'destructive' });
      return;
    }
    const amount = parseMoney(form.amount);
    if (!(amount > 0)) {
      toast({ title: 'Informe um valor válido', variant: 'destructive' });
      return;
    }
    if (!user) return;

    setSaving(true);
    try {
      const payload: any = {
        user_id: user.id,
        description: form.description.trim(),
        amount,
        entry_type: form.entry_type,
        category: form.category || null,
        due_date: form.due_date || null,
        status: 'Pendente',
        client_id: form.client_id || null,
        case_id: form.case_id || null,
        payment_confirmed: false,
      };
      const { error } = await supabase.from('financial_entries').insert(payload);
      if (error) throw error;

      toast({
        title:
          form.entry_type === 'receita'
            ? 'Lançamento a receber criado'
            : 'Lançamento a pagar criado',
        description: form.description,
      });
      onOpenChange(false);
      onCreated?.();
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const categories =
    form.entry_type === 'receita' ? CATEGORIES_INCOME : CATEGORIES_EXPENSE;

  return (
    <MobileFormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Novo lançamento"
      description="Cadastro rápido. Detalhes (parcelas etc) edite no desktop."
      footer={
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 h-12"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            className="flex-1 h-12"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Criar'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <Label className="text-xs font-semibold">Tipo</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button
              type="button"
              onClick={() => setForm({ ...form, entry_type: 'receita', category: '' })}
              className={`h-12 rounded-lg border text-sm font-semibold transition-colors ${
                form.entry_type === 'receita'
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : 'bg-card border-border'
              }`}
            >
              A receber
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, entry_type: 'despesa', category: '' })}
              className={`h-12 rounded-lg border text-sm font-semibold transition-colors ${
                form.entry_type === 'despesa'
                  ? 'bg-rose-500 border-rose-500 text-white'
                  : 'bg-card border-border'
              }`}
            >
              A pagar
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="fin-desc" className="text-xs font-semibold">
            Descrição *
          </Label>
          <Input
            id="fin-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Ex.: Honorários contrato João"
            className="h-12 mt-1"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="fin-amount" className="text-xs font-semibold">
              Valor *
            </Label>
            <Input
              id="fin-amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="R$ 0,00"
              inputMode="decimal"
              className="h-12 mt-1 font-bold tabular-nums"
            />
          </div>
          <div>
            <Label htmlFor="fin-due" className="text-xs font-semibold">
              Vencimento
            </Label>
            <Input
              id="fin-due"
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="h-12 mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold">Categoria</Label>
          <Select
            value={form.category}
            onValueChange={(v) => setForm({ ...form, category: v })}
          >
            <SelectTrigger className="h-12 mt-1">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs font-semibold">Cliente</Label>
          <Select
            value={form.client_id}
            onValueChange={(v) => setForm({ ...form, client_id: v })}
          >
            <SelectTrigger className="h-12 mt-1">
              <SelectValue placeholder="Opcional" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs font-semibold">Processo vinculado</Label>
          <Select
            value={form.case_id}
            onValueChange={(v) => setForm({ ...form, case_id: v })}
          >
            <SelectTrigger className="h-12 mt-1">
              <SelectValue placeholder="Opcional" />
            </SelectTrigger>
            <SelectContent>
              {cases.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </MobileFormSheet>
  );
};

export default MobileFinancialQuickForm;
